<template>
    <div>
        <notifications class="max-w-md select-none" position="bottom right" width="100%">
            <template slot="body" slot-scope="props">
                <div class="inset-0 flex items-end justify-center pointer-events-none sm:items-start sm:justify-end">
                    <div class="w-full mx-4 mb-4 bg-white rounded-lg pointer-events-auto">
                        <div class="rounded-lg shadow-md border border-gray-200 overflow-hidden">
                            <div class="p-4" @click="props.close">
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg-vue v-if="props.item.type === 'error'" class="h-6 w-6 text-red-400" icon="font-awesome.times-circle-light"></svg-vue>
                                        <svg-vue v-else-if="props.item.type === 'warning'" class="h-6 w-6 text-orange-400" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                        <svg-vue v-else-if="props.item.type === 'info'" class="h-6 w-6 text-blue-400" icon="font-awesome.info-circle-light"></svg-vue>
                                        <svg-vue v-else class="h-6 w-6 text-green-400" icon="font-awesome.check-circle-light"></svg-vue>
                                    </div>
                                    <div class="ml-3 w-0 flex-1 pt-0.5">
                                        <p class="text-sm leading-5 font-medium text-gray-900">
                                            {{ props.item.title }}
                                        </p>
                                        <p class="mt-1 text-sm leading-5 text-gray-500" v-html="props.item.text"/>
                                    </div>
                                    <div class="ml-4 flex-shrink-0 flex">
                                        <button class="inline-flex text-gray-400 focus:outline-none focus:text-gray-500 transition ease-in-out duration-150" type="button" @click="props.close">
                                            <svg-vue class="h-5 w-5 p-px" icon="font-awesome.times-light"></svg-vue>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </notifications>
        <router-view/>
    </div>
</template>

<script>
import {initPushNotifications, requestPushPermission} from '@/services/push';

export default {
    name: "App",
    data() {
        return {
            baseTitle: document.title,
            baseFaviconHref: null,
            unreadSyncHandler: null,
        }
    },
    computed: {
        notificationUnreadTotal() {
            return this.$store.state.notificationUnreadTotal || 0;
        }
    },
    watch: {
        notificationUnreadTotal(value) {
            this.applyTitleBadge(value);
            this.applyFaviconBadge(value);
        },
        '$store.state.user'(value) {
            if (value) {
                this.$nextTick(() => {
                    this.syncNotificationUnreadTotal();
                    if (window.Notification && window.Notification.permission === 'default' && !localStorage.getItem('ticketing-push-prompted')) {
                        localStorage.setItem('ticketing-push-prompted', '1');
                        requestPushPermission(this).catch(function () {});
                        return;
                    }
                    initPushNotifications(this).catch(function () {});
                });
            }
        },
        $route() {
            this.$nextTick(() => {
                this.baseTitle = document.title.replace(/^\(\d+\)\s*/, '');
                this.applyTitleBadge(this.notificationUnreadTotal);
            });
        }
    },
    mounted() {
        this.baseTitle = document.title.replace(/^\(\d+\)\s*/, '');
        this.applyTitleBadge(this.notificationUnreadTotal);
        const favicon = document.querySelector("link[rel*='icon']");
        this.baseFaviconHref = favicon ? favicon.href : (window.app && window.app.icon ? window.app.icon : '/favicon.ico');
        this.applyFaviconBadge(this.notificationUnreadTotal);
        this.unreadSyncHandler = () => {
            this.syncNotificationUnreadTotal(true);
        };
        window.addEventListener('focus', this.unreadSyncHandler);
        document.addEventListener('visibilitychange', this.onVisibilitySync);
        window.addEventListener('dashboard-refresh-notifications', this.unreadSyncHandler);
        if (this.$store.state.user) {
            this.syncNotificationUnreadTotal();
        }
    },
    beforeDestroy() {
        if (this.unreadSyncHandler) {
            window.removeEventListener('focus', this.unreadSyncHandler);
            window.removeEventListener('dashboard-refresh-notifications', this.unreadSyncHandler);
        }
        document.removeEventListener('visibilitychange', this.onVisibilitySync);
    },
    methods: {
        onVisibilitySync() {
            if (!document.hidden) {
                this.syncNotificationUnreadTotal(true);
            }
        },
        syncNotificationUnreadTotal(silent = false) {
            if (!this.$store.state.user || !this.$store.state.user.role || !this.$store.state.user.role.dashboard_access) {
                return;
            }

            axios.get('api/dashboard/notifications', {params: {limit: 1}}).then((response) => {
                this.$store.commit('setNotificationUnreadTotal', response.data.unread_total || 0);
            }).catch(() => {
                if (!silent) {
                    this.$store.commit('setNotificationUnreadTotal', this.$store.state.notificationUnreadTotal || 0);
                }
            });
        },
        applyTitleBadge(count) {
            const cleanTitle = (this.baseTitle || document.title).replace(/^\(\d+\)\s*/, '');
            if (count > 0) {
                document.title = '(' + count + ') ' + cleanTitle;
                return;
            }
            document.title = cleanTitle;
        },
        applyFaviconBadge(count) {
            if (navigator.setAppBadge) {
                if (count > 0) {
                    navigator.setAppBadge(count).catch(function () {});
                } else {
                    navigator.clearAppBadge().catch(function () {});
                }
            }

            const favicon = document.querySelector("link[rel*='icon']");
            if (!favicon || !this.baseFaviconHref) {
                return;
            }
            if (count < 1) {
                favicon.href = this.baseFaviconHref;
                return;
            }

            const image = new Image();
            image.crossOrigin = 'anonymous';
            image.onload = function () {
                const canvas = document.createElement('canvas');
                canvas.width = 64;
                canvas.height = 64;
                const context = canvas.getContext('2d');
                if (!context) {
                    favicon.href = this.baseFaviconHref;
                    return;
                }

                context.drawImage(image, 0, 0, 64, 64);
                context.fillStyle = '#dc2626';
                context.beginPath();
                context.arc(49, 15, 15, 0, Math.PI * 2);
                context.fill();
                context.fillStyle = '#ffffff';
                context.font = 'bold 20px sans-serif';
                context.textAlign = 'center';
                context.textBaseline = 'middle';
                context.fillText(count > 99 ? '99+' : String(count), 49, 15);
                favicon.href = canvas.toDataURL('image/png');
            }.bind(this);
            image.onerror = function () {
                favicon.href = this.baseFaviconHref;
            }.bind(this);
            image.src = this.baseFaviconHref;
        }
    },
}
</script>
